import SubHeader from "../../components/elements/SubHeader";
import Sidebar from "../../components/plan-compile/Sidebar";
import { CollapseContainerContextProvider } from "../../context/collapse-container.context";
import SubNav from "../../components/plan-template/SubNav";
import EditorContent from "../../components/plan-template/EditorContent";
import { useEffect, useState } from "react";
import LayoutSidebar from "../../components/layout/LayoutSidebar";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import usePlanCompile from "../../hooks/plan-compile.hook";
import GoalsContent from "../../components/plan-compile/GoalsContent";
import ImpersonificationLayer from "../../components/plan-compile/ImpersonificationLayer";
import IconContainer from "../../components/utils/IconContainer";
import EndSessionPopup from "../../components/popups/plan-compile/EndSessionPopup";
import ConfirmModal from "../../components/popups/ConfirmPopup";
import CrudApprovalPopup from "../../components/popups/plan-compile/CrudApprovalPopup";
import PlanOriginalTextPopup from "../../components/popups/PlanOriginalTextPopup";
import Prompt from "../../components/utils/Prompt";
import ActionBtnItem from "../../components/utils/ActionBtnItem";
import useCalendly from "../../hooks/calenldy.hook";
import PopoverActionStatus from "../../components/plan-template/PopoverActionStatus";
import { getAnnotations } from "../../services/agendas.service";
import PlanDownloadModal from "../../components/popups/PlanDownloadPopup";
import { getTemplatePlanContent } from "../../services/plans-template.service";
import { updatePlanContent } from "../../services/plans.service";
import TextareaInput from "../../components/input/TextareaInput";
import debounce from "lodash.debounce";
import useDateFormatter from "../../hooks/date-formatter.hook";
import { usePlanCompileStore } from "../../store/plan-compile.store";
import { formatOrganizationPlanDownloadObject } from "../../helper/formatter.helper";
import { ARRAY_STATUS_ICON } from "../../helper/common.helper";
import { SocketEcho } from "../../services/config";
import AgendaReader from "../../components/plan-compile/AgendaReader";
import { getSfadCourses } from "../../services/course.service";
import PlanAttachmentsPopup from "../../components/popups/PlanAttachmentsPopup";
import CreateTaskModal from "../../components/popups/task/CreateTaskPopup";
import TaskRowInfo from "./TaskRowInfo";
import MoreOptions from "../../components/utils/more-options/MoreOptions";
import { useSessionStore } from "../../store/session.store";
import { getScheduledEvents } from "../../services/supporto/schedule-events.services";

function PlanCompile({ role = "admin" }) {
	const planData = useLoaderData();

	const [searchParams] = useSearchParams();

	const { redirectPlan, saveActivities } = usePlanCompile();

	const { user } = useSessionStore();

	const {
		selectedContent,
		chapterList,
		isSavingContent,
		changesList,
		isEndSessionOpen,
		isConfirmOpen,
		isApprovalOpen,
		isPlanApproved,
		updateState,
		editContent,
		updateContentStatus,
		deleteConf,
		createTaskConf,
		selectContentHandler,
		trackChanges,
		reset,
	} = usePlanCompileStore();

	const { openCalendly } = useCalendly();

	const { formatDate, formatDateToTimestamp } = useDateFormatter();

	const navigate = useNavigate();

	const [loadingAnnotations, setLoadingAnnotations] = useState(false);

	const [showAgenda, setShowAgenda] = useState(false);

	const [usersPresence, setUsersPresence] = useState([]);

	const [annotationsList, setAnnotationsList] = useState([]);

	const [openPrinterModal, setOpenPrinterModal] = useState(false);

	const [openAttachmentsModal, setOpenAttachmentsModal] = useState(false);

	const [counterAttachments, setCounterAttachments] = useState(planData?.attachments?.length ?? 0);

	const [openResetContent, setOpenResetContent] = useState(false);

	const [resetCounter, setResetCounter] = useState(0);

	const [planOriginalTextPopupOpen, setPlanOriginalTextPopupOpen] = useState(false);

	const [planTasksCounter, setPlanTasksCounter] = useState(planData.todo_count ?? 0);

	const [scheduledEvents, setScheduledEvents] = useState([]);

	const downloadTemplate = formatOrganizationPlanDownloadObject(planData.organization_contents);

	useEffect(() => {
		reset();

		updateState({
			chapterList: planData.organization_contents,
			planDetail: planData,
			roleSettings: role,
			changesList: [],
		});

		getListAnnotations();

		// Init content by searchParams
		const searchChapter = Number(searchParams.get("chapter"));
		const searchGoal = Number(searchParams.get("goal"));
		const searchContent = Number(searchParams.get("content"));
		let item = null;

		if (!Number.isNaN(searchChapter)) {
			const chapterContent = planData.organization_contents.find(
				(item) => item.content_id === searchChapter
			);
			if (chapterContent) {
				if (!Number.isNaN(searchGoal) && searchGoal > 0) {
					item = chapterContent.data.organization_contents.find(
						(item) => item.content_id === searchGoal && item.content_type === "organization_goal"
					);
				} else if (!Number.isNaN(searchContent) && searchContent > 0) {
					item = chapterContent.data.organization_contents.find(
						(item) =>
							item.content_id === searchContent &&
							item.content_type === "organization_chapter_content"
					);
				}
			}

			if (item) {
				selectContentHandler(item);
			}
		}

		// Socket Users Presence
		if (role === "admin") {
			SocketEcho.join(`organization-plans.${planData.id}`)
				.here(initUserPresence)
				.joining(addUserPresence)
				.leaving(removeUserPresence)
				.error((error) => {
					console.error(error);
				});
		}

		return () => {
			if (role === "admin") {
				SocketEcho.leave(`organization-plans.${planData.id}`);
			}
		};
	}, []);

	useEffect(() => {
		window.addEventListener("beforeunload", preventNavigation);

		return () => {
			window.removeEventListener("beforeunload", preventNavigation);
		};
	}, [changesList]);

	useEffect(() => {
		window.addEventListener("unload", trackActivities);

		return () => {
			window.removeEventListener("unload", trackActivities);
		};
	}, [changesList]);

	useEffect(() => {
		getSfadCourses(undefined, (res) => {
			updateState({ courses: res?.data ?? [] });
		});

		return () => {
			saveActivities(planData);
		};
	}, []);

	useEffect(() => {
		if (["admin"].includes(role)) {
			getListScheduledEvents();
		}
	}, [role, planData.organization_id, planData.id]);

	/**
	 * Track activities
	 * @param {*} e
	 * @returns
	 */
	const trackActivities = (e) => {
		saveActivities(planData);
		e.preventDefault();
		return null;
	};

	const hasSessionChanges =
		changesList.length > 0 ||
		isPlanApproved ||
		!!trackChanges?.plansAttachments?.uploaded?.length ||
		!!trackChanges?.plansAttachments?.removed?.length;

	/**
	 * Prevent Reload
	 * @param {*} e
	 * @returns
	 */
	const preventNavigation = (e) => {
		if (hasSessionChanges) e.returnValue = true;
	};

	const arrayStatusContent = [
		{
			title: "Da gestire",
			icon: "uncompleted",
			value: "uncompleted",
			color: ARRAY_STATUS_ICON?.find((s) => s.icon === "uncompleted")?.color ?? "",
		},
		{
			title: "Completato",
			icon: "completed",
			value: "completed",
			color: ARRAY_STATUS_ICON?.find((s) => s.icon === "completed")?.color ?? "",
		},
	];

	const addUserPresence = (user) => {
		setUsersPresence((users) => [...users, user]);
	};

	const removeUserPresence = (user) => {
		setUsersPresence((users) => users.filter((item) => item.user.id !== user.user.id));
	};

	const initUserPresence = (items) => {
		setUsersPresence(items);
	};

	/**
	 * Template Nav
	 * @returns
	 */
	const templateNav = () => (
		<div className="main_comp_actions_group">
			<div className="main_list_status_container">
				{isSavingContent.length > 0 && (
					<IconContainer icon="loader opacity-70" tooltip="Salvataggio in corso" />
				)}

				{selectedContent.content_type === "organization_goal" &&
					selectedContent?.data?.template_goal?.status !== "default" && (
						<>
							<IconContainer
								icon={
									"icon_mask !bg-interactive-100 " +
									(selectedContent?.data?.template_goal?.status === "new" ? "megaphone" : "update")
								}
								tooltip={
									selectedContent?.data?.template_goal?.status === "new" ? "Nuovo" : "Modificato"
								}
							/>

							{selectedContent?.data?.organization_requirements.find(
								(el) => el.status === "uncompleted"
							) && <IconContainer icon="danger_corp" tooltip="Da gestire" />}
						</>
					)}

				{selectedContent?.content_type === "organization_chapter_content" &&
					selectedContent?.data?.status !== null && (
						<>
							{role === "admin" && (
								<>
									<ActionBtnItem
										icon="preview"
										big
										onClick={() => setPlanOriginalTextPopupOpen(true)}
										tooltip="Anteprima documento di ripristino"
									/>

									{formatDateToTimestamp(
										selectedContent?.data?.template_chapter_content?.updated_at
									) > formatDateToTimestamp(selectedContent?.data?.updated_at) && (
										<ActionBtnItem
											icon="danger"
											layout={"my-[4px] !bg-white"}
											iconLayout={""}
											small
											tooltip={`Template modificato il ${formatDate(
												selectedContent?.data?.template_chapter_content?.updated_at,
												"DD MMM YYYY, HH:mm"
											)}`}
										/>
									)}
								</>
							)}

							<PopoverActionStatus
								layout="small"
								onChange={(value) => updateContentStatus(value)}
								initStatus={selectedContent?.data?.status}
								arrayStatus={arrayStatusContent}
							/>

							{role === "admin" && (
								<button
									type="button"
									className=" link-red  text-12 ml-3"
									onClick={() => setOpenResetContent(true)}
								>
									Ripristina documento
								</button>
							)}
						</>
					)}
			</div>
		</div>
	);

	const templateHeader = () => (
		<div className="flex gap-4 items-center">
			{usersPresence.length > 1 && (
				<div
					data-tooltip-id="tooltip_small_top"
					data-tooltip-offset={10}
					data-tooltip-position-strategy="fixed"
					data-tooltip-content={usersPresence
						.map((item) => `${item.user.first_name} ${item.user.last_name}`)
						.join("\n")}
					className="flex items-center gap-3  fade_in bg-danger-100 rounded-full font-medium pl-3 pr-[1px] text-white py-[1px]"
				>
					<div className="flex items-center gap-2">
						<div className="icon_16 default user !bg-white icon_mask"></div>
						{usersPresence.length}
					</div>

					<div className="bg-white p-[6px] rounded-full flex">
						<div className="icon_16 default danger"></div>
					</div>
				</div>
			)}

			<div className="relative">
				<ActionBtnItem icon="attachments" onClick={() => setOpenAttachmentsModal(true)} big />
				{counterAttachments > 0 && (
					<div className="absolute -top-2 -right-2 bg-interactive-100 text-white rounded-full text-9 leading-[1em] font-bold w-4 h-4 flex items-center justify-center">
						{counterAttachments}
					</div>
				)}
			</div>

			{(role === "admin" || (role === "organization" && !!user?.fad_id)) && (
				<ActionBtnItem icon="support" onClick={openCalendly} big />
			)}

			<ActionBtnItem icon="printer" onClick={() => setOpenPrinterModal(true)} big />

			{!!planData.agenda && (
				<div
					className={
						"shadow-[inset_0_0_0_1px] shadow-interactive-50 flex items-center gap-3 rounded-[4px] p-[8px_10px] ml-[4px]"
					}
				>
					<button
						type="button"
						onClick={() => setShowAgenda(false)}
						aria-label="Nascondi agenda"
						className={
							"icon_16 default cursor-pointer book_disabled icon_mask !bg-interactive-100" +
							(showAgenda ? " opacity-30" : "")
						}
					></button>

					{/* Editable */}
					<div className="toggle_landing flex">
						<input
							checked={showAgenda}
							onChange={(e) => setShowAgenda(e.target.checked)}
							type="checkbox"
							id="copy_all_entity"
							className="landing"
						/>
						<label className="only_toggle" htmlFor="copy_all_entity">
							<span className="sr-only">Mostra agenda</span>
						</label>
					</div>

					<button
						type="button"
						onClick={() => setShowAgenda(true)}
						aria-label="Mostra agenda"
						className={
							"icon_16 default cursor-pointer book icon_mask !bg-interactive-100" +
							(!showAgenda ? " opacity-30" : "")
						}
					></button>
				</div>
			)}

			<MoreOptions
				dropdownContentProps={{
					className: "z-[30]",
				}}
				customTrigger={
					<button
						type="button"
						className="button line small whitespace-nowrap data-[state=open]:!outline outline-interactive-50"
					>
						<span className="icon_24 default options -my-1 -ml-2"></span>
						<span>Attività del piano</span>
					</button>
				}
				options={[
					{
						label: "Crea nuova attività",
						icon: "icon_16 icon_mask !bg-interactive-100 default plus",
						onClick: () => createTaskHandler("plan"),
					},
					{
						label: (
							<div className="flex justify-between w-full items-center">
								Vedi tutte le attività
								<div className="bg-interactive-100 text-white rounded-full text-9 leading-[8px] font-bold size-5 flex items-center justify-center">
									{planTasksCounter > 99 ? "+99" : planTasksCounter}
								</div>
							</div>
						),
						onClick: () => {
							window.open(
								`${process.env.REACT_APP_HOST_CLIENT_TODO}/plans/${planData.template_plan_id}/tasks${role !== "organization" ? `?organizations=${planData.organization_id}&scope=all` : ""}`,
								"_blank"
							);
						},
					},
				]}
			/>

			<button
				type="button"
				className="button ligth_green small fill"
				onClick={() => updateState({ isApprovalOpen: true })}
			>
				<span className="icon_24 default approved icon_mask !bg-success-100 -my-1"></span>
				Adotta il piano
			</button>

			{role === "organization" && (
				<button
					type="button"
					className="button primary small fill"
					onClick={() =>
						hasSessionChanges ? updateState({ isEndSessionOpen: true }) : navigateBack()
					}
				>
					Termina sessione
				</button>
			)}
		</div>
	);

	/**
	 * Cancel session
	 */
	const cancelSession = () => {
		updateState({ isEndSessionOpen: false, isConfirmOpen: true });
	};

	/**
	 * Get list annotations
	 */
	const getListAnnotations = () => {
		if (planData?.agenda?.id) {
			setLoadingAnnotations(true);

			const body = {
				id: planData.agenda.id,
				idCorp: planData.organization_id,
			};

			getAnnotations(
				body,
				(res) => {
					setAnnotationsList(res.data);
					setLoadingAnnotations(false);
				},
				() => setLoadingAnnotations(false)
			);
		}
	};

	/**
	 * Reset Content Document
	 */
	const resetContentDocument = () => {
		getTemplatePlanContent({ id: selectedContent.data.template_chapter_content_id }, (res) => {
			updatePlanContent(
				{
					id: selectedContent.content_id,
					idCorp: planData.organization_id,
					editorjs: res.data.editorjs,
					status: "uncompleted",
				},
				(res) => {
					const findChapt = chapterList.find(
						(c) => c.content_id === selectedContent.data.organization_chapter_id
					);

					const findContent = findChapt.data.organization_contents.find(
						(c) =>
							c.content_id === selectedContent.content_id &&
							c.content_type === selectedContent.content_type
					);

					findContent.data.status = "uncompleted";

					updateState({
						selectedContent: {
							...selectedContent,
							data: {
								...res.data,
							},
						},
						chapterList: [...chapterList],
					});

					setResetCounter((r) => r + 1);
					setOpenResetContent(false);
					setPlanOriginalTextPopupOpen(false);
				}
			);
		});
	};

	const handlerTrackPlanAttachments = (action = "upload", file) => {
		let currentUpload = trackChanges.plansAttachments.uploaded;
		const currentRemove = trackChanges.plansAttachments.removed;

		if (action === "upload") currentUpload.push(file);

		if (action === "remove") {
			if (currentUpload.find((item) => item.id === file.id))
				currentUpload = currentUpload.filter((item) => item.id !== file.id);
			else currentRemove.push(file);
		}

		updateState({
			trackChanges: {
				plansAttachments: {
					uploaded: currentUpload,
					removed: currentRemove,
				},
			},
		});
	};

	const navigateBack = () => {
		setTimeout(() => {
			if (role === "admin") {
				navigate(`/admin/entities/entity/${planData.organization_id}`);
			}
			if (role === "reseller") {
				navigate(`/reseller/${planData.organization_id}`);
			}
			if (role === "organization") {
				navigate(`/organization`);
			}
		}, 0);
	};

	const createTaskHandler = (type) => {
		const bodyParams = {
			...(type === "plan" && { organization_plan_id: planData.id }),
			...(type === "content" && {
				organization_chapter_content_id: selectedContent.content_id,
			}),
		};

		updateState({
			createTaskConf: {
				isOpen: true,
				bodyParams,
				onInsert: () =>
					type === "plan"
						? setPlanTasksCounter((c) => c + 1)
						: updateState({
								selectedContent: {
									...selectedContent,
									data: {
										...selectedContent.data,
										todo_count: selectedContent.data.todo_count + 1,
									},
								},
							}),
			},
		});
	};

	const getListScheduledEvents = async () => {
		try {
			const res = await getScheduledEvents({
				params: {
					filter: {
						organization_id: [planData.organization_id],
						organization_plan_id: [planData.id],
					},
				},
			});
			setScheduledEvents(res.data);
		} catch {}
	};

	return (
		<LayoutSidebar>
			<div>
				<SubHeader
					title={planData.template_plan.name}
					titleSmall
					description={planData.template_plan.description}
					TemplateRightContent={templateHeader}
				/>

				<CollapseContainerContextProvider>
					<Sidebar />
				</CollapseContainerContextProvider>

				<div className="main_page_sidebar">
					{selectedContent && (
						<>
							<SubNav
								category={
									selectedContent.content_type === "organization_chapter_content" && `Documento`
								}
								title={
									selectedContent.content_type === "organization_goal"
										? `${selectedContent.data.template_goal.name} – ${selectedContent.data.template_goal.description}`
										: selectedContent.data.template_chapter_content.name
								}
								TemplateRightContent={templateNav}
							/>

							<div className="wrap_page_sidebar">
								{selectedContent.content_type === "organization_chapter_content" ? (
									<div
										key={selectedContent.content_id + resetCounter}
										className={`bg-white rounded-[4px]`}
									>
										<div className="p-[32px_28px_0_86px] max-w-[1200px] mx-auto">
											<div className="shadow-[inset_0_-1px] shadow-dark-10 pb-[28px]">
												<TaskRowInfo
													counter={selectedContent?.data?.todo_count}
													createHandler={() => createTaskHandler("content")}
													templatePlanId={planData.template_plan_id}
													organizationId={planData.organization_id}
												/>
											</div>
										</div>
										{selectedContent.data.status !== null && role === "admin" ? (
											<div className={`p-[28px_28px_0_86px] max-w-[1200px] mx-auto`}>
												<div className={`font-semibold text-[12px] text-dark-50 mb-[8px]`}>
													Note amministrative
												</div>
												<TextareaInput
													model={selectedContent.data.admin_note}
													onChange={debounce(
														(data) =>
															editContent(
																"adminNote",
																data.target.value,
																selectedContent.content_id
															),
														400
													)}
													className={""}
												/>
											</div>
										) : (
											selectedContent.data.admin_note && (
												<div className={`p-[32px_28px_0_86px] max-w-[1200px] mx-auto`}>
													<div className="p-5 bg-dark-5 rounded-[4px]">
														<div className={`font-semibold text-[12px] text-dark-50`}>Note</div>
														<div className={`text-[14px] text-dark-80`}>
															{selectedContent.data.admin_note}
														</div>
													</div>
												</div>
											)
										)}

										<EditorContent
											initVal={selectedContent.data.editorjs}
											onChange={debounce(
												(data) => editContent("editorjs", data, selectedContent.content_id),
												400
											)}
											headerLevels={[2]}
											readOnly={selectedContent.data.status == null}
										/>
									</div>
								) : (
									<GoalsContent />
								)}
							</div>
						</>
					)}
				</div>
			</div>

			{/* Close session */}
			<ConfirmModal
				open={isConfirmOpen}
				setOpen={(isOpen) => updateState({ isConfirmOpen: isOpen })}
				title="Termina sessione senza salvare"
				alertMessage="Attenzione! Hai scelto di non salvare i dati della sessione. <b>Sei sicuro di procedere?</b>"
				ctaLabel="Termina"
				onConfirm={() => redirectPlan(role)}
			/>

			{/* Reset content */}
			<ConfirmModal
				open={openResetContent}
				setOpen={setOpenResetContent}
				title="Ripristina documento"
				alertMessage="Attenzione! Ripristinando il documento perderai tutte le modifiche fatte. <b>Sei sicuro di procedere?</b>"
				ctaLabel="Conferma"
				onConfirm={resetContentDocument}
			/>

			{/* Approval popup */}
			<CrudApprovalPopup
				open={isApprovalOpen}
				plan={planData}
				onSuccess={(data) => updateState({ isPlanApproved: data })}
				setOpen={(isOpen) => updateState({ isApprovalOpen: isOpen })}
			/>

			{/* Plan original popup */}
			<PlanOriginalTextPopup
				open={planOriginalTextPopupOpen}
				selectedContent={selectedContent?.data}
				setOpen={setPlanOriginalTextPopupOpen}
				setReset={setOpenResetContent}
			/>

			<Prompt when={hasSessionChanges} message="Vuoi abbandonare la sessione?" />

			<EndSessionPopup
				open={isEndSessionOpen}
				setOpen={(isOpen) => updateState({ isEndSessionOpen: isOpen })}
				onCancel={cancelSession}
				agenda={planData.agenda}
				annotations={annotationsList.filter((annotation) => !!annotation?.user_can_update)}
				loadingAnnotations={loadingAnnotations}
				role={role}
				scheduledEvents={scheduledEvents}
				organizationId={planData.organization_id}
			/>

			{(role === "admin" || role === "reseller") && (
				<ImpersonificationLayer
					organization={planData.organization}
					onEndSession={() =>
						hasSessionChanges ? updateState({ isEndSessionOpen: true }) : navigateBack()
					}
				/>
			)}

			{showAgenda && !!planData.agenda && (
				<AgendaReader
					planData={planData}
					agenda={planData.agenda}
					className={role === "organization" ? " !bottom-[40px]" : ""}
				/>
			)}

			{/* Confirm content */}
			<ConfirmModal
				open={deleteConf.isOpen}
				setOpen={(isOpen) => updateState({ deleteConf: { ...deleteConf, isOpen } })}
				title={deleteConf.title}
				icon={deleteConf.icon}
				subtitle={deleteConf.subtitle}
				description={deleteConf.description}
				alertMessage="Attenzione! Questa operazione è irreversibile! <b>Sei sicuro di procedere?</b>"
				ctaLabel="Elimina"
				onConfirm={() => deleteConf.confirmHandler(deleteConf.item)}
			/>

			<PlanDownloadModal
				open={openPrinterModal}
				setOpen={setOpenPrinterModal}
				template={downloadTemplate}
				idOrganization={planData.organization_id}
				idPlan={planData.id}
				statuses={planData.organization_requirements_statuses}
				templateId={planData.template_plan_id}
			/>

			<PlanAttachmentsPopup
				open={openAttachmentsModal}
				setOpen={setOpenAttachmentsModal}
				attachments={planData?.attachments ?? []}
				organizationPlan={planData}
				onUpdateList={(list) => setCounterAttachments(list?.length ?? 0)}
				onUpload={(file) => handlerTrackPlanAttachments("upload", file)}
				onRemove={(file) => handlerTrackPlanAttachments("remove", file)}
			/>

			<CreateTaskModal
				organizationId={planData.organization_id}
				open={createTaskConf?.isOpen}
				bodyParams={createTaskConf.bodyParams}
				setOpen={(isOpen) => updateState({ createTaskConf: { ...createTaskConf, isOpen } })}
				onInsert={createTaskConf.onInsert}
			/>
		</LayoutSidebar>
	);
}

export default PlanCompile;
